import { convertoUpperSnakeCase } from './../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/helpers';
import { useMyLeadsCRUDActions } from '@mm/state/actions/MyLeadsCRUDActions';
import {
  MyLeadsSearchQuery,
  SubsectionKeys,
} from '@mm/state/interfaces/myLeads';
import { selectMyLeadsSearchOptions } from '@mm/state/selectors/myLeadsSelectors';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePrevious } from '../../../../../common/hooks/use-previous';

export const useFilterMyLeads = ({
  filterParams,
  fetchAttributes = false,
}: {
  fetchAttributes?: boolean;
  filterParams: MyLeadsSearchQuery;
}) => {
  const { retrieveAttributes, searchLeads } = useMyLeadsCRUDActions();
  const [busy, setBusy] = useState(false);

  const searchOptions = useSelector(selectMyLeadsSearchOptions);
  const searchVariablesFiltered = Object.entries(
    searchOptions.variables || {},
  ).filter(item => item[1].length);
  const searchVariablesList = Object.fromEntries(searchVariablesFiltered);
  const searchAttributes = searchOptions.attributes;
  const businessContactKey = convertoUpperSnakeCase(SubsectionKeys.leadType);

  useEffect(() => {
    (async () => {
      if (fetchAttributes) {
        setBusy(true);
        await retrieveAttributes({ skipBusy: true });
        setBusy(false);
      }
    })();
  }, []);

  const debouncedLeadsSearch = useCallback(
    () =>
      debounce(async () => {
        setBusy(true);

        const isExcludingBusinessContacts =
          !!filterParams.query?.excludedVariables?.[businessContactKey];
        const isIncludingBusinessContacts =
          !!filterParams.query?.variables?.[businessContactKey];
        const updatedFilterParams = filterParams;

        if (isExcludingBusinessContacts && isIncludingBusinessContacts) {
          delete updatedFilterParams.query?.excludedVariables?.[
            businessContactKey
          ];
          delete updatedFilterParams.query?.variables?.[businessContactKey];
        }

        await searchLeads(filterParams, true);
        setBusy(false);
      }, 300),
    [searchLeads, filterParams, businessContactKey],
  );

  const previousDebouncedSearch = usePrevious(debouncedLeadsSearch());

  useEffect(() => {
    previousDebouncedSearch?.cancel();
  }, [previousDebouncedSearch]);

  useEffect(() => {
    debouncedLeadsSearch()();
  }, [filterParams, debouncedLeadsSearch]);

  return {
    searchAttributes,
    searchVariablesList,
    busy,
  };
};
